/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal, Dropdown, Button, Segment } from "semantic-ui-react";
import { Checkbox, TextField } from "@mui/material";
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { format, parseISO } from 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import { changeTool } from "../slices/footer/footerSlice";
import { selectCurrentUserApiToken, selectCurrentUserId, selectCurrentUserName } from "../slices/currentUser/currentUserSlice";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { selectSearchSettings } from "../slices/searchSettings/searchSettingsSlice";
import { changeFoiFocus } from "../slices/foiOfFocus/foiOfFocusSlice";
import getSearchResults from "../functions/getSearchResultsAPI";
import submitFhirJson from "../functions/submitFhirJsonAPI";
import CodeArrayEntry from "../datatypeComponents/CodeArrayEntry";
import AlertModal from "./AlertModal";
import SaveSearchModal from "./SaveSearchModal";
import ProfilesByResourceType from "../dictionaries/ProfilesByResourceType";
import { allFevirResourceTypes } from "../dictionaries/CodeSystemLookup";
import retireSelectedResources from "../functions/retireSelectedResourcesAPI";
import inviteToEditSelectedResources from "../functions/inviteToEditSelectedResourcesAPI";


const CalendarModal = ({ startingValue, elementName, calendarOpen, setCalendarOpen, setSearchQueryState }) => {
    return <span className="calendarModal" style={{ position: "absolute !important", top: 0, bottom: 0, height: "0px !important" }}>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
                format="yyyy-MM-dd"
                emptyLabel="yyyy-MM-dd"
                value={startingValue ? parseISO(startingValue, "yyyy-MM-dd") : null}
                open={calendarOpen}
                onClose={() => setCalendarOpen(false)}
                onChange={(e) => {
                    setSearchQueryState(prevState => { return { ...prevState, [elementName]: format(e, "yyyy-MM-dd") } });
                }}
            />
        </MuiPickersUtilsProvider>
    </span>
};

const DateEntry = ({ elementName, fieldLabel, startingValue, setSearchQueryState, width }) => {
    const [calendarOpen, setCalendarOpen] = useState(false);
    return <>
        <TextField style={{ width: "100%", marginTop: "12px", maxWidth: width ? width : "260px" }}
            className="inputField" type='text' label={fieldLabel + " (YYYY-MM-DD)"}
            size="small" variant='outlined'
            value={startingValue || ""}
            onChange={(e) => {
                setSearchQueryState(prevState => { return { ...prevState, [elementName]: e.target.value } });
            }} />
        <Button style={{
            position: "relative", zIndex: 20, marginTop: "18px", marginLeft: "-38px", fontSize: "28px", padding: "0px",
            backgroundColor: "white"
        }} className="calendarButton"
            content="📅" onClick={() => { setCalendarOpen(true) }} />
        <CalendarModal startingValue={startingValue} elementName={elementName}
            calendarOpen={calendarOpen} setCalendarOpen={setCalendarOpen}
            setSearchQueryState={setSearchQueryState} />
    </>
}

const InviteModal = ({ inviteText, setInviteText }) => {
    const content = <>
        <Button style={{ padding: "6px", position: "absolute", top: "6px", right: "6px" }}
            className="formButton negative" content="✖"
            onClick={() => { setInviteText(null) }} />
        <div style={{
            width: "100%", padding: "6px", background: "#FFFFFF",
            border: "1px solid #000000", whiteSpace: "pre-wrap"
        }}>{inviteText}</div>
        <br />
        <Button className="formButton" style={{ color: "#000000" }}
            content="Copy Invite to Clipboard"
            onClick={() => { navigator.clipboard.writeText(inviteText) }} />
        <br /><br />
    </>;
    return <Modal
        style={{ width: "80%", margin: "16px" }}
        dimmer={<Modal.Dimmer blurring style={{ backgroundColor: "#00000077" }} />}
        open={inviteText}
        centered={false}
        content={content} />
}

// eslint-disable-next-line no-empty-pattern
const SearchQueryPage = ({ }) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userName = useSelector(selectCurrentUserName);
    const userId = useSelector(selectCurrentUserId);
    const isLoggedIn = (!!userId && userId !== "1");
    const searchSettings = useSelector(selectSearchSettings);

    const [searchQueryState, setSearchQueryState] = useState({ "simpleString": "", "searchType": "All Content", "runSavedSearch": false, "population": "", "intervention": "", "comparator": "", "outcome": "" });
    const [currentSearchResultsState, setCurrentSearchResultsState] = useState(false);
    const [fevirSearchResultsState, setFevirSearchResultsState] = useState([]);
    const [searchResultsLoadingState, setSearchResultsLoadingState] = useState(false);
    const [profileValuesState, setProfileValuesState] = useState([]);
    const [alertModalState, setAlertModalState] = useState({
        open: false, alertHistory: [], header: "", content: ""
    });
    const [saveSearchResultsClickedState, setSaveSearchResultsClickedState] = useState(false);
    const [openSaveSearchModalState, setOpenSaveSearchModalState] = useState(false);
    const [limitToMyResourcesState, setLimitToMyResourcesState] = useState(false);
    const [creatingInviteState, setCreatingInviteState] = useState(false);
    const [inviteText, setInviteText] = useState("");

    useEffect(() => {
        dispatch(changeBrowserTabTitle("FEvIR Search"));
        dispatch(changeTool("FEvIR® Search"));
    }, []);

    useEffect(() => {
        if (searchSettings.source === "fevir") {
            let searchQuery = {
                "runSavedSearch": true,
                "simpleString": searchSettings.searchstring,
                "searchType": searchSettings.searchparameters?.searchType || "All Content",
                "resourceTypeLimiter": searchSettings.searchparameters?.resourceTypeLimiter,
                "selectedProfiles": searchSettings.searchparameters?.selectedProfiles,
                "population": searchSettings.searchparameters?.population,
                "intervention": searchSettings.searchparameters?.intervention,
                "comparator": searchSettings.searchparameters?.comparator,
                "outcome": searchSettings.searchparameters?.outcome || searchSettings.searchparameters?.["outcome-measure"],
                "outcome-measure": searchSettings.searchparameters?.outcome || searchSettings.searchparameters?.["outcome-measure"]
            };
            if (searchSettings.searchparameters?.updatedSearchStartDate) {
                let startdate = searchSettings.searchparameters.updatedSearchStartDate.replaceAll("/", "-");
                let enddate = searchSettings.searchparameters.updatedSearchEndDate?.replaceAll("/", "-");
                searchQuery.earliestDate = startdate;
                searchQuery.latestDate = enddate;
            }
            setSearchQueryState(searchQuery);
        }
    }, [searchSettings]);

    useEffect(() => {
        if (searchQueryState.runSavedSearch) {
            searchClick();
        }
    }, [searchQueryState.runSavedSearch]);

    useEffect(() => {
        if (searchQueryState.resourceTypeLimiter?.length) {
            let availableProfiles = [];
            for (const resourceType of searchQueryState.resourceTypeLimiter) {
                if (ProfilesByResourceType[resourceType]) {
                    availableProfiles = availableProfiles.concat(Object.keys(ProfilesByResourceType[resourceType]));
                }
            }
            if (availableProfiles.length) {
                setProfileValuesState(availableProfiles);
            }
        } else if (profileValuesState?.length) {
            setProfileValuesState([]);
            setSearchQueryState(prevState => { return { ...prevState, "selectedProfiles": [] } });
        }
    }, [searchQueryState.resourceTypeLimiter]);

    useEffect(() => {
        if (currentSearchResultsState) {
            searchClick();
        }
    }, [limitToMyResourcesState]);

    const processSearchSelections = async (simpleString = "", searchType = "") => {
        setCurrentSearchResultsState(false);
        simpleString = simpleString || searchQueryState.simpleString;
        searchType = searchType || searchQueryState.searchType;
        let earliestDate = searchQueryState.earliestDate || undefined;
        let latestDate = searchQueryState.latestDate || undefined;

        let searchResults = await getSearchResults(simpleString,
            searchType,
            searchQueryState.resourceTypeLimiter,
            searchQueryState.selectedProfiles,
            searchQueryState.population,
            searchQueryState.intervention,
            searchQueryState.comparator,
            searchQueryState.outcome, //"outcome-measure"
            earliestDate,
            latestDate,
            limitToMyResourcesState,
            apiToken); //[searchResults, includedResourceTypeValues] 

        setFevirSearchResultsState(searchResults);
        setCurrentSearchResultsState(true);
        setSearchResultsLoadingState(false);
    }

    const searchClick = (simpleString = "", searchType = "") => {
        setFevirSearchResultsState([]);
        setSearchResultsLoadingState(true);
        processSearchSelections(simpleString, searchType);
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) { //So it doesn't do a line break when you hit enter, you can still do it if you hit the shift key
            event.preventDefault(); //Prevents the line break in the TextField
            if (searchQueryState.simpleString?.trim() || searchQueryState.population?.trim() || searchQueryState.intervention?.trim() || searchQueryState.comparator?.trim() || searchQueryState.outcome?.trim()) {  //"outcome-measure"
                searchClick();
            }
        }
    }

    const submitLibraryResource = async (fhirJson, title) => {
        let response = await submitFhirJson(fhirJson, title, apiToken);
        let resourceFOI;
        if (response?.success) {
            resourceFOI = response.formstateid;
        } else {
            setAlertModalState(prevState => {
                return {
                    ...prevState,
                    "open": true,
                    "header": "Error",
                    "content": "Problem creating Library of resulting Resources."
                };
            });
            return null;
        }
        dispatch(changeFoiFocus(resourceFOI));
        navigate(`/resources/Library/${resourceFOI}`);
    }

    useEffect(() => {
        if (saveSearchResultsClickedState === true) {
            let searchString = searchQueryState.simpleString ||
                searchQueryState.selectedProfiles?.join(",") ||
                searchQueryState.resourceTypeLimiter?.join(",");
            let title = "FEvIR Search Results for " + searchString.substring(0, 40) + " for " + userName;
            let fhirJson = {
                "resourceType": "Library", "title": title, "status": "active",
                "type": {
                    "coding": [{
                        "system": "http://terminology.hl7.org/CodeSystem/library-type",
                        "code": "asset-collection", "display": "Asset Collection"
                    }]
                },
                "date": new Date().toISOString(),
                "description": "",
                "relatedArtifact": []
            };

            if (fevirSearchResultsState?.length > 0) {
                fhirJson.description = "Your search includes " + fevirSearchResultsState.length + " Resources.";
                for (const result of fevirSearchResultsState) {
                    fhirJson.relatedArtifact.push({
                        "type": "composed-of",
                        "resourceReference": result.data
                    });
                }
            } else {
                fhirJson.description = "No Resources were found on your search.";
            }
            fhirJson.description += " Your search string was: " + searchQueryState.simpleString + ". ";
            if (searchQueryState.selectedProfiles?.length > 0) {
                fhirJson.description += "Your search results were limited to Profiles of: " + searchQueryState.selectedProfiles.join(", ") + ".";
            }
            if (searchQueryState.resourceTypeLimiter?.length > 0) {
                fhirJson.description += "Your search results were limited to Resources of type: " + searchQueryState.resourceTypeLimiter.join(", ") + ".";
            }
            submitLibraryResource(fhirJson, title);
        }
    }, [saveSearchResultsClickedState]);

    return <div>
        <AlertModal alertModalState={alertModalState} setAlertModalState={setAlertModalState} />
        <SaveSearchModal openSaveSearchModalState={openSaveSearchModalState}
            setOpenSaveSearchModalState={setOpenSaveSearchModalState}
            searchParameters={{ searchType: searchQueryState.searchType, resourceTypeLimiter: searchQueryState.resourceTypeLimiter, selectedProfiles: searchQueryState.selectedProfiles, population: searchQueryState.population, intervention: searchQueryState.intervention, comparator: searchQueryState.comparator, outcome: searchQueryState.outcome }}
            source={"fevir"} searchString={searchQueryState.simpleString}
            header={"Label your FEvIR search"}
            searched={currentSearchResultsState} />
        {inviteText &&
            <InviteModal inviteText={inviteText} setInviteText={setInviteText} />
        }
        <Segment className={`containerSegment searchSegment maxRemainderOfPageSegment`} textAlign="left" raised>
            <TextField className="inputField" type="text" size="small" multiline
                style={{ width: "70%", fontSize: "16px !important" }}
                label="Simple Search String" variant="outlined"
                value={searchQueryState.simpleString || ""}
                onKeyDown={handleKeyDown}
                onChange={(e) => { setSearchQueryState(prevState => { return { ...prevState, "simpleString": e.target.value } }); }}
            />
            <Dropdown
                name="searchType"
                placeholder="Search Type"
                closeOnChange selection selectOnBlur={false} autoComplete='off'
                style={{ marginLeft: "8px", fontWeight: "bold", minWidth: "220px" }}
                options={[
                    { key: "All Content", text: "All Content", value: "All Content" },
                    { key: "Content & Element Names", text: "Content & Element Names", value: "Content & Element Names" },
                    { key: "Titles", text: "Titles", value: "Titles" },
                    /*{ key: "Titles & Descriptsions", text: "Titles & Descriptsions", value: "Titles & Descriptsions" }*/
                ]}
                value={searchQueryState.searchType || ''}
                onChange={(e, data) => {
                    setSearchQueryState(prevState => { return { ...prevState, "searchType": data.value } });
                    if (searchQueryState.simpleString?.trim()) {
                        searchClick(searchQueryState.simpleString, data.value); //data.value is needed here and not searchQueryState.searchType becauase searchQueryState.searchType isn't done setting yet
                    }
                }}
            />
            <br /><br />
            <div style={{position: "relative", zIndex: 40}}>
                <CodeArrayEntry elementName='resourceTypeLimiter' fieldLabel='Limit by Resource Type'
                    dataEntryStyle='dropdownsearch'
                    allowedValues={allFevirResourceTypes}
                    startingValue={searchQueryState.resourceTypeLimiter || []}
                    setResourceState={setSearchQueryState} />
            </div>
            {/*<p style={{ paddingTop: "12px" }}>Limit to resource types of: {(searchQueryState.resourceTypeLimiter &&
                searchQueryState.resourceTypeLimiter.length > 0) ?
                searchQueryState.resourceTypeLimiter.join(', ') : "[No limits]"}</p>*/}
            <br />
            {profileValuesState.length ?
                <div style={{position: "relative", zIndex: 30}}>
                    <CodeArrayEntry elementName='selectedProfiles' fieldLabel='Limit by Profile'
                        dataEntryStyle='dropdownsearch'
                        allowedValues={profileValuesState.sort()}
                        startingValue={searchQueryState.selectedProfiles || []}
                        setResourceState={setSearchQueryState} />
                    {/*<p style={{ paddingTop: "12px" }}>
                        Limit to Profiles of: {(searchQueryState.selectedProfiles &&
                            searchQueryState.selectedProfiles.length > 0) ?
                            searchQueryState.selectedProfiles.join(', ') : "[No limits]"}</p>*/}
                    <br />
                </div>
                :
                <></>
            }
            {(!searchQueryState.resourceTypeLimiter || searchQueryState.resourceTypeLimiter.length === 0 || searchQueryState.resourceTypeLimiter.includes("Evidence") || searchQueryState.resourceTypeLimiter.includes("Composition")) && <div>
                <span style={{ verticalAlign: "middle" }}>Limit by PICO:</span>
                <TextField className="inputField textInputSmallerFont textSmaller" type="text" size="small"
                    style={{ width: "200px", maxWidth: "20%", fontSize: "16px !important", marginLeft: "8px" }}
                    label="Population" variant="outlined"
                    value={searchQueryState.population || ""}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => { setSearchQueryState(prevState => { return { ...prevState, "population": e.target.value } }); }}
                />
                <TextField className="inputField textInputSmallerFont textSmaller" type="text" size="small"
                    style={{ width: "200px", maxWidth: "20%", fontSize: "16px !important", marginLeft: "8px" }}
                    label="Intervention" variant="outlined"
                    value={searchQueryState.intervention || ""}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => { setSearchQueryState(prevState => { return { ...prevState, "intervention": e.target.value } }); }}
                />
                <TextField className="inputField textInputSmallerFont textSmaller" type="text" size="small"
                    style={{ width: "200px", maxWidth: "20%", fontSize: "16px !important", marginLeft: "8px" }}
                    label="Comparator" variant="outlined"
                    value={searchQueryState.comparator || ""}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => { setSearchQueryState(prevState => { return { ...prevState, "comparator": e.target.value } }); }}
                />
                <TextField className="inputField textInputSmallerFont textSmaller" type="text" size="small"
                    style={{ width: "200px", maxWidth: "20%", fontSize: "16px !important", marginLeft: "8px" }}
                    label="Outcome" variant="outlined"
                    value={searchQueryState.outcome || ""}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => { setSearchQueryState(prevState => { return { ...prevState, "outcome": e.target.value } }); }} //"outcome-measure"
                />
                <br /><br />
            </div>}
            {isLoggedIn &&
                <div className="unselectable" style={{ cursor: "pointer", marginBottom: "12px" }}
                    onClick={() => {
                        setLimitToMyResourcesState(prevState => {
                            let checked = !prevState;
                            return checked;
                        });
                    }}>
                    <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                        checked={limitToMyResourcesState} size="small" color="primary" />
                    <span>&nbsp;Limit to My Resources</span></div>
            }
            <div>
                <DateEntry elementName='earliestDate' setSearchQueryState={setSearchQueryState}
                    fieldLabel={'Earliest Date Revised'} startingValue={searchQueryState.earliestDate} width={"300px"} />
                <span style={{ paddingLeft: "8px" }}></span>
                <DateEntry elementName='latestDate' setSearchQueryState={setSearchQueryState}
                    fieldLabel={'Latest Date Revised'} startingValue={searchQueryState.latestDate} width={"300px"} />
                <br /><br />
            </div>
            <span>
                <Button style={{ color: "#000000", width: "360px", float: "left" }}
                    className="formButton ballotButton"
                    content="Search"
                    onClick={() => { searchClick(); }} />
                &nbsp;&nbsp;
                <span style={{ display: 'inline-block', marginTop: "2px" }}>
                    <Button className="formButton positive" content={"Save Search"} compact
                        onClick={() => {
                            if (apiToken) {
                                setOpenSaveSearchModalState(true);
                            } else {
                                setAlertModalState(prevState => {
                                    return {
                                        ...prevState,
                                        "open": true,
                                        "header": "Please login",
                                        "content": "Log into FEvIR to use this function."
                                    };
                                });
                            }
                        }}
                    />
                </span>
                {(!searchResultsLoadingState && !creatingInviteState &&
                    currentSearchResultsState && fevirSearchResultsState?.length > 0 &&
                    !limitToMyResourcesState)
                    &&
                    <>
                        &nbsp;&nbsp;
                        <span style={{ display: 'inline-block', marginTop: "2px" }}>
                            <Button className="formButton positive" content={"Save Search Results"} compact
                                onClick={() => {
                                    if (apiToken) {
                                        setSaveSearchResultsClickedState(true);
                                    } else {
                                        setAlertModalState(prevState => {
                                            return {
                                                ...prevState,
                                                "open": true,
                                                "header": "Please login",
                                                "content": "Log into FEvIR to use this function."
                                            };
                                        });
                                    }
                                }}
                            />
                        </span>
                    </>}
            </span>
            <br />   <br />
            {(searchResultsLoadingState || creatingInviteState) ?
                <><img style={{ height: "22px" }} src="/spinner.gif" alt="Loading" /> Loading...</>
                :
                <div style={{ marginTop: "18px" }}>
                    <hr />
                    {currentSearchResultsState &&
                        <>{(fevirSearchResultsState?.length > 0) ?
                            <>{limitToMyResourcesState === true ?
                                <>
                                    <p>
                                        {fevirSearchResultsState.length} FEvIR search results.
                                    </p>
                                    <Button content="Select All"
                                        onClick={() => {
                                            setFevirSearchResultsState(prevState =>
                                                prevState.map(result => ({ ...result, selectedToRetire: true }))
                                            );
                                        }} />&nbsp;&nbsp;
                                    <Button content="Deselect All"
                                        onClick={() => {
                                            setFevirSearchResultsState(prevState =>
                                                prevState.map(result => ({ ...result, selectedToRetire: false }))
                                            );
                                        }} />&nbsp;&nbsp;
                                    <Button content="Retire Selected Resources"
                                        onClick={() => { retireSelectedResources(fevirSearchResultsState, apiToken); }} />&nbsp;&nbsp;
                                    <Button content="Invite to Edit Selected Resources"
                                        onClick={async () => {
                                            setCreatingInviteState(true);
                                            const inviteText = await inviteToEditSelectedResources(fevirSearchResultsState, userName, apiToken);
                                            if (inviteText) {
                                                setInviteText(inviteText);
                                            }
                                            setCreatingInviteState(false);
                                        }} />
                                    {fevirSearchResultsState.map((result, resultIndex) => {
                                        return <p key={resultIndex} style={{ marginBottom: "6px" }}>
                                            <a href={result.onClick} >{result.display}</a>
                                            <span>{result.status === "draft" && <>&nbsp;&nbsp;(draft)</>}</span>
                                            <span className="unselectable" style={{ cursor: "pointer", marginLeft: "12px" }}
                                                onClick={() => {
                                                    setFevirSearchResultsState(prevState => {
                                                        const updatedEntry = { ...prevState[resultIndex], selectedToRetire: !prevState[resultIndex].selectedToRetire };
                                                        return prevState.with(resultIndex, updatedEntry);
                                                    });
                                                }}>
                                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                                    checked={!!result.selectedToRetire} size="small" color="primary" />
                                                <span>&nbsp;Select</span></span>
                                        </p>
                                    })}
                                </>
                                :
                                <>
                                    <p>
                                        {fevirSearchResultsState.length} FEvIR search results.
                                    </p>
                                    <br /><br />
                                    {fevirSearchResultsState.map((result, resultIndex) => {
                                        return <p key={resultIndex} style={{ marginBottom: "6px" }}>
                                            <a href={result.onClick} >{result.display}</a><span>{result.status === "draft" && <>&nbsp;&nbsp;(draft)</>}</span>
                                        </p>
                                    })}
                                </>
                            }</>
                            :
                            "No matching entries on FEvIR Platform."
                        }</>
                    }</div>
            }
        </Segment>
    </div>
}

export default SearchQueryPage;